import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// ---- Auth (JWT bearer in localStorage) ----
const TOKEN_KEY = "crm_auth_token";

export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);
export const setAuthToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

api.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// When the token expires or is invalid, blow it away and bounce to /login.
// The AuthContext also listens for the "crm-auth-expired" event.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && !url.includes("/auth/")) {
      setAuthToken(null);
      window.dispatchEvent(new CustomEvent("crm-auth-expired"));
    }
    return Promise.reject(err);
  }
);

// Emergency master-password path (disabled by default server-side; returns 403)
export const authLogin = (password) =>
  api.post("/auth/login", { password }).then((r) => r.data);
// Google Sign-In: exchange the session_id for our own JWT
export const authGoogleSession = (session_id) =>
  api.post("/auth/google/session", { session_id }).then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);

// Folders (containers)
export const listFolders = () => api.get("/folders").then((r) => r.data);
export const createFolder = (payload) => api.post("/folders", payload).then((r) => r.data);
export const updateFolder = (id, payload) => api.patch(`/folders/${id}`, payload).then((r) => r.data);
export const deleteFolder = (id) => api.delete(`/folders/${id}`).then((r) => r.data);

// Tags (leaf labels)
export const listTags = () => api.get("/tags").then((r) => r.data);
export const createTag = (payload) => api.post("/tags", payload).then((r) => r.data);
export const updateTag = (id, payload) => api.patch(`/tags/${id}`, payload).then((r) => r.data);
export const deleteTag = (id) => api.delete(`/tags/${id}`).then((r) => r.data);
export const reorderTags = (items) => api.post("/tags/reorder", { items }).then((r) => r.data);

// Contacts
export const listContacts = (tagId) => {
  const params = tagId !== undefined ? { tag_id: tagId } : {};
  return api.get("/contacts", { params }).then((r) => r.data);
};
export const createContact = (payload) => api.post("/contacts", payload).then((r) => r.data);
export const updateContact = (id, payload) => api.patch(`/contacts/${id}`, payload).then((r) => r.data);
export const deleteContact = (id) => api.delete(`/contacts/${id}`).then((r) => r.data);

// ---- POI library (separate from contacts) ----
export const listPoiFolders = () => api.get("/poi_folders").then((r) => r.data);
export const createPoiFolder = (payload) => api.post("/poi_folders", payload).then((r) => r.data);
export const updatePoiFolder = (id, payload) => api.patch(`/poi_folders/${id}`, payload).then((r) => r.data);
export const deletePoiFolder = (id) => api.delete(`/poi_folders/${id}`).then((r) => r.data);

export const listPoiTags = () => api.get("/poi_tags").then((r) => r.data);
export const createPoiTag = (payload) => api.post("/poi_tags", payload).then((r) => r.data);
export const updatePoiTag = (id, payload) => api.patch(`/poi_tags/${id}`, payload).then((r) => r.data);
export const deletePoiTag = (id) => api.delete(`/poi_tags/${id}`).then((r) => r.data);
export const reorderPoiTags = (items) => api.post("/poi_tags/reorder", { items }).then((r) => r.data);

export const listPois = (tagId) => {
  const params = tagId !== undefined ? { tag_id: tagId } : {};
  return api.get("/pois", { params }).then((r) => r.data);
};
export const createPoi = (payload) => api.post("/pois", payload).then((r) => r.data);
export const updatePoi = (id, payload) => api.patch(`/pois/${id}`, payload).then((r) => r.data);
export const deletePoi = (id) => api.delete(`/pois/${id}`).then((r) => r.data);

// ---- Bookings (journal/meet planning per contact) ----
export const listBookings = (params = {}) =>
  api.get("/bookings", { params }).then((r) => r.data);
export const createBooking = (payload) =>
  api.post("/bookings", payload).then((r) => r.data);
export const updateBooking = (id, payload) =>
  api.patch(`/bookings/${id}`, payload).then((r) => r.data);
export const deleteBooking = (id) =>
  api.delete(`/bookings/${id}`).then((r) => r.data);

// ---- Audio transcription (Whisper) ----
export const transcribeAudio = (blob, filename = "audio.webm") => {
  const form = new FormData();
  form.append("file", blob, filename);
  return api
    .post("/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};


// Nominatim search (OpenStreetMap) — worldwide coverage
const NOMINATIM = "https://nominatim.openstreetmap.org";
const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

function _addressToParts(a = {}) {
  const town =
    a.city || a.town || a.village || a.hamlet || a.suburb || a.neighbourhood || a.locality || "";
  const cc = (a.country_code || "").toLowerCase();
  // For Canada / US the meaningful sub-national unit is the state/province,
  // not the county. UK / others still prefer county where it exists.
  const region =
    cc === "ca" || cc === "us"
      ? a.state || a.state_district || a.region || ""
      : a.county || a.state_district || a.state || a.region || "";
  const postcode = a.postcode || "";
  const country = a.country || "";
  const street = [a.house_number, a.road].filter(Boolean).join(" ");
  return { street, town, region, postcode, country };
}

export async function searchPlaces(query) {
  if (!query || query.trim().length < 2) return [];
  const params = new URLSearchParams({
    q: query,
    format: "json",
    addressdetails: "1",
    limit: "10",
  });
  const res = await fetch(`${NOMINATIM}/search?${params.toString()}`, {
    headers: { "Accept-Language": "en" },
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data.map((d) => {
    const parts = _addressToParts(d.address);
    return {
      id: d.place_id,
      name: d.display_name,
      short: d.name || d.display_name.split(",")[0],
      type: d.type,
      lat: parseFloat(d.lat),
      lng: parseFloat(d.lon),
      ...parts,
    };
  });
}

/**
 * Contextual autocomplete. Combines a free-text `query` with any already-known
 * `context` (country / region / town) so, for example, typing "Dundas" while
 * the user has picked Canada + Ontario + London first narrows results to
 * London, Ontario, Canada instead of returning globally scattered Dundas
 * streets. If Nominatim returns nothing with the context, we transparently
 * fall back to a plain global search so the user still sees suggestions.
 */
export async function searchPlacesScoped(query, context = {}) {
  const q = (query || "").trim();
  if (q.length < 2) return [];
  const bits = [q, context.town, context.region, context.country]
    .map((s) => (s || "").trim())
    .filter(Boolean);
  // Dedupe (e.g. if user typed "London" and town is also "London")
  const seen = new Set();
  const scopedQuery = bits
    .filter((s) => {
      const k = s.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .join(", ");
  const scoped = await searchPlaces(scopedQuery);
  if (scoped.length) return scoped;
  // Fallback: broader query so the user isn't stuck if the context is wrong.
  if (scopedQuery !== q) {
    return await searchPlaces(q);
  }
  return [];
}

export async function reverseGeocode(lat, lng) {
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lng),
    format: "json",
    addressdetails: "1",
    zoom: "16",
  });
  try {
    const res = await fetch(`${NOMINATIM}/reverse?${params.toString()}`, {
      headers: { "Accept-Language": "en" },
    });
    if (!res.ok) return null;
    const d = await res.json();
    if (!d || !d.address) return null;
    const parts = _addressToParts(d.address);
    return {
      display_name: d.display_name || "",
      short: d.name || parts.town || (d.display_name || "").split(",")[0],
      ...parts,
    };
  } catch {
    return null;
  }
}

async function _lookupUkPostcode(postcode) {
  try {
    const res = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.trim().toUpperCase())}`
    );
    const json = await res.json();
    if (!json) return null;
    const r = json.result || json.terminated;
    if (!r || r.latitude == null || r.longitude == null) return null;
    return {
      lat: r.latitude,
      lng: r.longitude,
      postcode: r.postcode || postcode.trim().toUpperCase(),
      town: r.post_town || r.admin_district || r.parish || "",
      region: r.admin_county || r.admin_district || r.region || "",
      country: r.country || "United Kingdom",
      street: "",
      short: r.post_town || r.admin_district || r.postcode || postcode.trim().toUpperCase(),
      display_name: [r.post_town, r.admin_district, r.postcode || postcode, r.country]
        .filter(Boolean)
        .join(", "),
    };
  } catch {
    return null;
  }
}

async function _nominatimSearch({ street, town, region, postcode, country }) {
  const params = new URLSearchParams({
    format: "json",
    addressdetails: "1",
    limit: "1",
  });
  if (postcode) params.set("postalcode", postcode);
  if (town) params.set("city", town);
  if (street) params.set("street", street);
  // For CA/US the sub-national unit is the state; for UK/others use county.
  const cLow = (country || "").toLowerCase();
  const isCaUs = cLow.includes("canada") || cLow.includes("united states");
  if (region) params.set(isCaUs ? "state" : "county", region);
  if (country) params.set("country", country);

  try {
    let res = await fetch(`${NOMINATIM}/search?${params.toString()}`, {
      headers: { "Accept-Language": "en" },
    });
    let data = res.ok ? await res.json() : [];

    if (!data.length) {
      const q = [street, town, region, postcode, country]
        .filter(Boolean)
        .join(", ")
        .trim();
      if (!q) return null;
      const fp = new URLSearchParams({
        q,
        format: "json",
        addressdetails: "1",
        limit: "1",
      });
      res = await fetch(`${NOMINATIM}/search?${fp.toString()}`, {
        headers: { "Accept-Language": "en" },
      });
      data = res.ok ? await res.json() : [];
    }

    if (!data.length) return null;
    const d = data[0];
    const parts = _addressToParts(d.address || {});
    if (postcode && UK_POSTCODE_RE.test(postcode)) parts.postcode = postcode.toUpperCase();
    return {
      lat: parseFloat(d.lat),
      lng: parseFloat(d.lon),
      display_name: d.display_name || "",
      short: d.name || parts.town || (d.display_name || "").split(",")[0],
      ...parts,
    };
  } catch {
    return null;
  }
}

export async function geocodeAddress({ street, town, region, postcode, country }) {
  const cleanPostcode = postcode?.trim();
  if (cleanPostcode && UK_POSTCODE_RE.test(cleanPostcode)) {
    const pc = await _lookupUkPostcode(cleanPostcode);
    if (pc) {
      if (street?.trim() && town?.trim()) {
        const fine = await _nominatimSearch({ street, town, postcode: cleanPostcode, country });
        if (fine) return { ...pc, ...fine, postcode: cleanPostcode.toUpperCase() };
      }
      return pc;
    }
  }
  return _nominatimSearch({ street, town, region, postcode: cleanPostcode, country });
}

// AI natural-language search
export const searchAi = (query, preFilteredIds = null, history = null) =>
  api
    .post("/search/ai", {
      query,
      pre_filtered_ids: preFilteredIds,
      history,
    })
    .then((r) => r.data);

